import {useLocation, useNavigate} from "react-router-dom";
import {useLegacyLoginMutation} from "../../store/auth/auth-store";
import {AuthRequest, getLegacyAuthHeader} from "@ots-frontend/ots-data";
import {useAppThunkDispatch} from "../../store/hooks/useAppThunkDispatch";
import {useCallback} from "react";
import {useAuth0} from "@auth0/auth0-react";
import {setAuthToken} from "../../store/auth/actions";
import {AppLoginPage, useAuth} from "@ots-frontend/ots-ui";
import HubGoLogo from "../../../assets/hubgo.svg?react";
import OtsLogo from "../../../assets/ots-logo.svg?react";

export function LoginPage() {
  const auth = useAuth();

  const dispatch = useAppThunkDispatch();
  const navigate = useNavigate();
  const location = useLocation()

  const [loginLegacy, {isLoading: isLegacyLoading, error: legacyError}] = useLegacyLoginMutation();

  const {loginWithRedirect, isAuthenticated} = useAuth0();

  const params = new URLSearchParams(location.search);
  const returnTo = params.get("returnTo") || "/container-status";

  const onLegacyFormSubmit = useCallback(async (values: { username: string | undefined; password: string | undefined }) => {
    const request: AuthRequest = {username: values.username, password: values.password};
    const result = await loginLegacy(request).unwrap();
    dispatch(setAuthToken(getLegacyAuthHeader(result)));
    navigate(decodeURIComponent(returnTo), {replace: true});
  }, [dispatch, loginLegacy, navigate, returnTo])

  const onAuth0Login = useCallback(() => {
    const finalReturnTo = returnTo || "/container-status"
    loginWithRedirect({
      appState: {returnTo: finalReturnTo},
      authorizationParams: {
        prompt: "login",
      }
    });
  }, [loginWithRedirect, returnTo])

  if (auth.authenticated) {
    navigate("/container-status", {replace: true});
  }

  return (
    <AppLoginPage
      brandLogo={
        <HubGoLogo
          aria-label="HubGo"
          className="h-12 w-auto max-w-[240px]"
        />
      }
      poweredBy={{
        logo: <OtsLogo className="h-3.5 w-3.5"/>,
        name: "Oilfield Tracking Services",
        url: "https://oilfieldtrackingservices.com",
      }}
      usernameLabel="Username"
      usernameDescription="Use the username associated with your account."
      usernamePlaceholder="username"
      signInButtonLabel="Sign in with username"
      externalError={legacyError}
      isLegacyLoading={isLegacyLoading}
      isSsoAuthenticated={isAuthenticated}
      onLegacySubmit={onLegacyFormSubmit}
      onSsoLogin={onAuth0Login}
      marketingEyebrow="HubGo Dashboard"
      marketingHeadline={(
        <>
          Centralize operations across{" "}
          <span className="underline decoration-[#E1251B]/60 decoration-2 underline-offset-4">
            pipelines, trucking, and inventory
          </span>
          .
        </>
      )}
      marketingDescription={(
        <>
          HubGo brings operational teams into one workspace for dispatch requests,
          tickets, pipeline activity, settlements, volumes, and field insights.
          Sign in to manage the daily flow of work across your organization.
        </>
      )}
      marketingFeatures={[
        {
          title: "Operations data in one workspace",
          description: "Review tickets, container status, pipeline flows, production volumes, and inventory without jumping between tools.",
        },
        {
          title: "Role-aware access for every team",
          description: "Keep users focused on the companies, regions, assets, and workflows they are allowed to manage.",
        },
        {
          title: "Connected with the OTS platform",
          description: "HubGo works with the broader OTS ecosystem so field activity, financial review, and operational reporting stay aligned.",
        },
      ]}
      marketingFooter={(
        <p className="mt-8 text-[0.7rem] text-slate-500">
          Need access to another yard or tenant? Contact your HubGo administrator.
        </p>
      )}
    />
  )
}
